import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { TextInput, SwitchInput } from '@/components/atoms'
import { IpBlockModel } from '@/types'
import { IpBlockFormState, ipBlockFieldsConfig } from './ipBlockFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface IpBlockDialogProps {
    open: boolean
    mode: 'create' | 'edit' | 'view'
    editIpBlock: IpBlockModel.IpBlock | null
    closeDialog: () => void
    onCreate: (data: IpBlockFormState) => Promise<void>
    onUpdate: (id: string, data: Partial<IpBlockFormState>) => Promise<void>
    loading: boolean
}

export const IpBlockDialog = ({
    open,
    mode,
    editIpBlock,
    closeDialog,
    onCreate,
    onUpdate,
    loading = false,
}: IpBlockDialogProps) => {
    const {
        handleSubmit,
        reset,
        formState: { errors },
        control,
        watch,
    } = useForm<IpBlockFormState>({
        defaultValues: {
            ip: '',
            status: 'active',
        },
    })

    const formValues = watch()
    const { fields } = ipBlockFieldsConfig({ formValues })

    useEffect(() => {
        if ((mode === 'edit' || mode === 'view') && editIpBlock) {
            reset({
                ip: editIpBlock.ip,
                status: editIpBlock.status,
            })
        } else {
            reset({
                ip: '',
                status: 'active',
            })
        }
    }, [mode, editIpBlock, reset, open])

    const onSubmit = async (values: IpBlockFormState) => {
        if (mode === 'create') {
            await onCreate(values)
        } else if (mode === 'edit' && editIpBlock) {
            await onUpdate(editIpBlock._id, values)
        }
    }

    useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

    return (
        <Dialog open={open} onOpenChange={closeDialog}>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Block IP address' : mode === 'edit' ? 'Edit blocked IP' : 'Blocked IP'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Enter the IP address you want to block from accessing your store.'
                            : mode === 'edit'
                                ? 'Update the blocked IP address information.'
                                : 'View blocked IP address details.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <TextInput
                        control={control}
                        name="ip"
                        label="IP address"
                        placeholder="e.g. 192.168.1.1"
                        disabled={mode === 'view' || loading}
                        errors={errors}
                        rules={fields.ip.rules}
                    />

                    <SwitchInput
                        control={control}
                        name="status"
                        label="Status"
                        description={formValues.status === 'active' ? 'IP is blocked' : 'IP block is inactive'}
                        disabled={mode === 'view' || loading}
                        activeValue="active"
                        inactiveValue="inactive"
                        rules={fields.status.rules}
                    />
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
                        {mode === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {mode !== 'view' && (
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleSubmit(onSubmit)}
                            disabled={loading}>
                            {loading && <Spinner />}
                            {mode === 'create' ? 'Block IP' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
